import { useNavigate } from 'react-router-dom';
import Button from '../components/button/Button';
import Card from '../components/card/Card';
import H from '../components/texts/h/H';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  getApplications,
  postCommitteeBulkDecision,
  BulkAction,
  BulkApplicationItem,
  BulkDecisionResponse,
} from '../services/ApplicationService';
import P from '../components/texts/p/P';
import Image from '../components/image/Image';
import Table from '../components/table/table';
import { APPLICATION_STATUS } from '../utils/Constants';
import Input from '../components/input/Input';
import { IItem, ITableData } from '../types/interfaces';
import { hasContent, normalizeCuitForSearch } from '../utils/Utils';
import { getDashboardColumns } from '../utils/dashboardColumns';
import TableSkeleton from '../components/TableSkeleton/TableSkeleton';
import { useDebounce } from 'use-debounce';
import { useOfficers } from '../hooks/useOfficers';
import { routes } from '../routes/routePaths';
import Icon from '../components/icon/Icon';
import FilterModal from '../components/modals/FilterModal';
import { getFilters } from '../services/ApplicationService';
import { getRoles } from '../services/UserService';
import type { CommitteeRoleFilterItem } from '../components/modals/FilterModal/FilterModal.types';
import { FilterData } from '../components/modals/FilterModal/FilterModal.types';
import { useConfig } from '../context/ConfigContext';
import { getCommitteeCommentMaxLength } from '../utils/committeeApprovalComment';
import { parseGrantedTotalsFromApplicationListItem } from '../utils/grantedDashboardAmounts';
import { commercialCellFromOfficerPath } from '../utils/dashboardListCommercialCell';
import { resolveLocalCurrencyCode } from '../utils/quotationAssets';
import useRole from '../hooks/useRole';
import { useToast } from '../components/toast/toast';
import CommitteeBulkActionsBar from '../organisms/Committee/BulkActions/CommitteeBulkActionsBar';
import CommitteeBulkConfirmModal from '../organisms/Committee/BulkActions/CommitteeBulkConfirmModal';
import CommitteeBulkResultModal from '../organisms/Committee/BulkActions/CommitteeBulkResultModal';
import DeclineOrRequestChangesModal from '../components/modal/DeclineOrRequestChangesModal';
import { shouldHideCommitteeDeclineOption } from '../utils/committeeMemberRole';
import { getErrorCopy } from '../utils/bulkErrorMessages';
import {
  getPageSelectableIds,
  isMasterCheckboxChecked,
  togglePageSelection,
} from '../utils/committeeBulkPageSelection';
import { useRestoreListState } from '../hooks/useRestoreListState';
import { buildListState, DASHBOARD_KEYS } from '../utils/listNavigationState';

/** Estado de la state machine del aprobador masivo (Phase 8 - sección 4.3-ter
 * del plan SDD). `idle` cubre también el estado `selecting` del diagrama, ya
 * que la barra de acciones siempre está visible cuando hay rol+flag. */
type BulkPhase =
  | 'idle'
  | 'confirmApprove'
  | 'commentReject'
  | 'processing'
  | 'result';

const BULK_TOAST_ID = 'committee-bulk-toast';

/**
 * Mapea un item del listado a la fila de la tabla.
 *
 * Vive fuera del componente y es pura a propósito: `officers` sólo afecta el
 * label de la columna Comercial, así que la derivación corre en render y no
 * forma parte de las dependencias del fetch. Tenerla en el `useEffect` hacía
 * que el listado se pidiera dos veces por carga (`officers` cambia de
 * referencia cuando `useOfficers` resuelve).
 *
 * `canApprove`, `committeeMode` y `committeeHasNext` vienen del enrichment del
 * BFF cuando `status === 'COM'` + flag activo. Van en camelCase para coincidir
 * con `IRow` del componente `Table` (la API que pasa el row al cell renderer).
 */
const mapItemToApplication = (
  item: IItem,
  officers: ReadonlyArray<{ code: string; label: string }>,
  isManagementSelectionEnabled: boolean,
) => {
  const granted = parseGrantedTotalsFromApplicationListItem(item);
  return {
    cuit: item.cuit,
    id: item.id,
    commercial: commercialCellFromOfficerPath(
      item,
      officers,
      isManagementSelectionEnabled,
    ),
    companyName: item.company_name,
    producerName: item.producer.first_name,
    producerLastName: item.producer.last_name,
    updateDate: item.updated_date,
    status: item.status.id,
    statusCode: item.status.code,
    totalArs: item.total_ars,
    totalUsd: item.total_usd,
    approved_total_ars: item.approved_total_ars,
    approved_total_usd: item.approved_total_usd,
    granted_total_ars: granted.granted_total_ars,
    granted_total_usd: granted.granted_total_usd,
    usuario: item.producer.first_name + ' ' + item.producer.last_name,
    referido: (item as any).referido || 'N/A',
    pendingApprovalRoleLabel: (item as any).pending_approval_role_label ?? '',
    canApprove: item.committee_can_approve ?? false,
    committeeMode: item.committee_mode ?? 'LEGACY',
    committeeHasNext: item.committee_has_next,
  };
};

const CommitteeDashboard = () => {
  const appConfig = useConfig();
  const { isComite, isCommercial, isInstrumentator, isAdmin, isRiskAnalyst, isCommitteeOnly } = useRole();
  const isMultipleApprovalInCommitteeEnabled =
    appConfig.isMultipleApprovalInCommitteeEnabled ?? false;
  const isCommitteeMemberQuorumEnabled =
    appConfig.isCommitteeMemberQuorumEnabled === true;
  /** Cementos LEGACY multi-step: rol del paso pendiente. No aplica a Innova MEMBER_QUORUM. */
  const showPendingApprovalColumn = isMultipleApprovalInCommitteeEnabled;
  const isMaxCreditCalculatorEnabled = appConfig.isMaxCreditCalculatorEnabled === true;
  const isManagementSelectionEnabled = appConfig.isManagementSelectionEnabled ?? false;
  const committeeCommentMaxLength = getCommitteeCommentMaxLength(appConfig);
  /** Phase 8 gating: flag global + rol con permiso de aprobación. La
   * habilitación por fila vive en `committee_can_approve` (BFF enrichment). */
  const isBulkEnabled =
    appConfig.isCommitteeBulkApprovalEnabled === true &&
    (isComite || isCommercial || isInstrumentator || isCommitteeOnly);
  const navigate = useNavigate();
  const name = localStorage.getItem('name');
  const { officers } = useOfficers();
  const restoredList = useRestoreListState(DASHBOARD_KEYS.committee);
  /** Items crudos del listado. Las filas se derivan en render (ver
   * `mapItemToApplication`) para que `officers` no dispare un refetch. */
  const [rawItems, setRawItems] = useState<IItem[]>([]);
  const [tableData, setTableData] = useState<ITableData>();
  const [page, setPage] = useState<number>(restoredList.page);
  const [searchText, setSearchText] = useState<string>(restoredList.searchText);
  const [isSearching, setIsSearching] = useState<boolean>(false);
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [debouncedSearchText] = useDebounce(searchText, 300);
  const [isFilterModalOpen, setIsFilterModalOpen] = useState<boolean>(false);
  const [activeFilters, setActiveFilters] = useState<Record<string, string>>(
    restoredList.activeFilters,
  );
  const [filterData, setFilterData] = useState<FilterData | null>(null);
  const [committeeRoleFilterItems, setCommitteeRoleFilterItems] = useState<
    CommitteeRoleFilterItem[]
  >([]);
  const [isClearFiltersHovered, setIsClearFiltersHovered] =
    useState<boolean>(false);
  const lastRequest = useRef(0);

  // Aprobador masivo (Phase 8) — state machine + retry context.
  const [selectedIds, setSelectedIds] = useState<Set<number>>(restoredList.selectedIds);
  const [bulkPhase, setBulkPhase] = useState<BulkPhase>('idle');
  const [bulkRejectComment, setBulkRejectComment] = useState<string>('');
  const [lastBulkAction, setLastBulkAction] = useState<BulkAction | null>(null);
  const [lastDescription, setLastDescription] = useState<string>('');
  const [lastResult, setLastResult] = useState<BulkDecisionResponse | null>(
    null,
  );
  const [retryCount, setRetryCount] = useState<number>(0);

  /** Filas de la tabla. Se recalculan cuando llegan los items o cuando resuelve
   * el catálogo de oficiales, sin volver a pedir el listado al backend. */
  const applications = useMemo(
    () =>
      rawItems
        .filter((item: IItem) => item.status.code === 'COM')
        .map((item: IItem) =>
          mapItemToApplication(item, officers, isManagementSelectionEnabled),
        ),
    [rawItems, officers, isManagementSelectionEnabled],
  );

  useEffect(() => {
    const loadFilters = async () => {
      try {
        const response = await getFilters('application');
        if (response.success && 'data' in response) {
          setFilterData(response.data);
        }
      } catch (error) {
        console.error('Error loading filters:', error);
      }
    };
    loadFilters();
  }, []);

  useEffect(() => {
    if (!isMultipleApprovalInCommitteeEnabled) {
      setCommitteeRoleFilterItems([]);
      return;
    }
    let cancelled = false;
    (async () => {
      try {
        const res = await getRoles('committee-filter');
        if (cancelled || !res.success || !Array.isArray(res.data)) return;
        const items: CommitteeRoleFilterItem[] = res.data
          .map((r: { roleCode?: string; label?: string }) => ({
            roleCode: String(r.roleCode ?? ''),
            label: String(r.label ?? ''),
          }))
          .filter((r) => r.roleCode.length > 0);
        setCommitteeRoleFilterItems(items);
      } catch {
        if (!cancelled) setCommitteeRoleFilterItems([]);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [isMultipleApprovalInCommitteeEnabled]);

  /**
   * Fetch del listado de comité. Extraído del primer `useEffect` para que el
   * cleanup post-bulk (Phase 8 — D4 refresh in-place) pueda re-disparar la
   * misma carga sin tocar `page` ni `activeFilters` (preserva contexto del
   * usuario). El segundo flujo de búsqueda (`handleSearch`) sigue como estaba
   * para no romper el debounce existente.
   */
  const fetchCommittee = useCallback(() => {
    setIsLoading(true);
    const requestId = Date.now();
    lastRequest.current = requestId;
    getApplications(
      page,
      [APPLICATION_STATUS.committee.label],
      normalizeCuitForSearch(debouncedSearchText),
      activeFilters
    )
      .then((response) => {
        if (lastRequest.current !== requestId) return;
        if (hasContent(response)) {
          setTableData({
            size: response.data.size,
            page: response.data.page,
            totalElements: response.data.totalElements,
            totalPages: response.data.totalPages + 1,
          });

          setRawItems(response.data.content);
        } else {
          setRawItems([]);
        }
        setIsLoading(false);
        setIsSearching(false);
      })
      .catch(() => {
        setRawItems([]);
        setIsLoading(false);
        setIsSearching(false);
      });
  }, [page, debouncedSearchText, activeFilters]);

  useEffect(() => {
    fetchCommittee();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [page, debouncedSearchText, JSON.stringify(activeFilters)]);

  const handleChangePage = (data: number) => {
    if (data < 0 || data === tableData?.totalPages) {
      return;
    }
    setPage(data);
  };

  const handleRowClicked = (data: any) => {
    navigate(routes.committee + '/' + data.cuit + '/applications/' + data.id, {
      state: {
        status: data.status,
        statusCode: data.statusCode,
        listState: buildListState({
          dashboardKey: DASHBOARD_KEYS.committee,
          page,
          searchText,
          activeFilters,
          selectedIds,
        }),
      },
    });
  };

  const handleToggleRow = (id: number) => {
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) {
        next.delete(id);
      } else {
        next.add(id);
      }
      return next;
    });
  };

  const pageSelectableIds = useMemo(
    () => getPageSelectableIds(applications),
    [applications],
  );

  const handleTogglePageAll = useCallback(() => {
    setSelectedIds((prev) => togglePageSelection(pageSelectableIds, prev));
  }, [pageSelectableIds]);

  const columns = getDashboardColumns({
    handleRowClicked,
    showPendingApproval: showPendingApprovalColumn,
    showMontoOtorgarColumn: isMaxCreditCalculatorEnabled,
    commercialColumnTitle: isManagementSelectionEnabled ? 'Gerencia' : 'Comercial',
    showBulkSelection: isBulkEnabled,
    selectedIds,
    onToggleRow: handleToggleRow,
    isRowSelectable: (row) => row.canApprove === true,
    isMasterCheckboxChecked: isMasterCheckboxChecked(
      pageSelectableIds,
      selectedIds,
    ),
    isMasterCheckboxDisabled: pageSelectableIds.length === 0,
    onTogglePageAll: handleTogglePageAll,
    country: appConfig.country,
    localCurrencyCode: resolveLocalCurrencyCode(appConfig),
  });

  const handleSearch = () => {
    setIsSearching(true);
    const requestId = Date.now();
    lastRequest.current = requestId;
    getApplications(
      page,
      [APPLICATION_STATUS.committee.label],
      normalizeCuitForSearch(debouncedSearchText),
      activeFilters
    )
      .then((response) => {
        if (lastRequest.current !== requestId) return;
        if (hasContent(response)) {
          setTableData({
            size: response.data.size,
            page: response.data.page,
            totalElements: response.data.totalElements,
            totalPages: response.data.totalPages + 1,
          });

          setRawItems(response.data.content);
        } else {
          setRawItems([]);
        }
        setIsLoading(false);
        setIsSearching(false);
      })
      .catch(() => {
        setRawItems([]);
        setIsLoading(false);
        setIsSearching(false);
      });
  };

  /**
   * Aprobador masivo (Phase 8) — orquesta el POST al BFF.
   *
   * Se acepta un `applicationsOverride` opcional para soportar el reintento
   * (`handleRetryFailed`): cuando viene, el bulk envía sólo los items
   * recuperables filtrados desde `lastResult.failed` en lugar de derivarlos
   * de `selectedIds`. La selección original ya quedó "consumida" por el primer
   * intento y no debe seguir gobernando los retries.
   *
   * Manejo de error global del endpoint (network/500 antes del response):
   * la opción **B** del plan SDD (Phase 8 §8.8) — preferimos un toast de
   * error simple y volver a `idle` en lugar de fabricar un `lastResult`
   * sintético. El usuario ve la falla y puede reintentar manualmente
   * volviendo a clickear "Aprobar"/"Declinar". Esto evita disfrazar un fallo
   * total como un resultado parcial confuso en el modal de resultado.
   */
  const executeBulk = async (
    action: BulkAction,
    description: string,
    applicationsOverride?: BulkApplicationItem[],
  ) => {
    let applicationsPayload: BulkApplicationItem[];
    if (applicationsOverride) {
      applicationsPayload = applicationsOverride;
    } else {
      applicationsPayload = [];
      selectedIds.forEach((id) => {
        const found = applications.find((a) => a.id === id);
        if (found) {
          applicationsPayload.push({
            cuit: found.cuit,
            id: found.id,
            companyName: found.companyName,
          });
        }
      });
    }

    if (applicationsPayload.length === 0 || applicationsPayload.length > 10) {
      useToast.error({
        title: 'Selección inválida',
        description: 'Seleccioná entre 1 y 10 carpetas para aplicar la acción.',
        id: BULK_TOAST_ID,
        autoClose: 5000,
        closeButton: true,
        toastId: BULK_TOAST_ID,
      });
      return;
    }

    setBulkPhase('processing');

    try {
      const result = await postCommitteeBulkDecision({
        action,
        applications: applicationsPayload,
        description: description || undefined,
      });
      setLastBulkAction(action);
      setLastDescription(description);
      setLastResult(result);
      // Reset retryCount only when this is a fresh bulk (no override).
      if (!applicationsOverride) {
        setRetryCount(0);
      }
      setBulkPhase('result');
    } catch (error) {
      console.error('Error en bulk-decision:', error);
      useToast.error({
        title: 'No pudimos procesar el bulk',
        description: 'Reintentá en unos segundos. Si persiste, contactá al soporte.',
        id: BULK_TOAST_ID,
        autoClose: 5000,
        closeButton: true,
        toastId: BULK_TOAST_ID,
      });
      setBulkPhase('idle');
    }
  };

  const handleClickApprove = () => setBulkPhase('confirmApprove');
  const handleClickReject = () => {
    setBulkRejectComment('');
    setBulkPhase('commentReject');
  };

  const handleConfirmApprove = () => {
    void executeBulk('APPROVE', '');
  };

  const handleBulkDecline = (comment: string) => {
    setBulkRejectComment('');
    void executeBulk('REJECT', comment);
  };

  const handleBulkRequestChanges = (comment: string) => {
    setBulkRejectComment('');
    void executeBulk('REQUEST_CHANGES', comment);
  };

  const handleRetryFailed = () => {
    if (!lastResult || !lastBulkAction) return;
    const retryable = lastResult.failed.filter(
      (item) => getErrorCopy(item.errorCode).isRetryable,
    );
    if (retryable.length === 0) return;
    if (retryCount >= 3) return;
    const retryApplications: BulkApplicationItem[] = retryable.map((item) => ({
      cuit: item.cuit,
      id: item.id,
      companyName: item.companyName,
    }));
    setRetryCount((c) => c + 1);
    void executeBulk(lastBulkAction, lastDescription, retryApplications);
  };

  /**
   * Cleanup post-bulk (D4 — refresh in-place sin `navigate`). Cierra el modal,
   * limpia selección + retry context y vuelve a disparar `fetchCommittee()`
   * preservando page + filtros + scroll del usuario.
   */
  const handleCloseResultModal = () => {
    setBulkPhase('idle');
    setSelectedIds(new Set());
    setRetryCount(0);
    setLastResult(null);
    setLastBulkAction(null);
    setLastDescription('');
    fetchCommittee();
  };

  const handleOpenModal = () => {
    setIsFilterModalOpen(true);
  };

  const handleCloseModal = () => {
    setIsFilterModalOpen(false);
  };

  const handleApplyFilters = (queryParams: Record<string, string>) => {
    setSelectedIds(new Set());
    setActiveFilters(queryParams);
    setPage(0); // Resetear a la primera página cuando se aplican filtros
  };

  const handleClearFilters = () => {
    setSelectedIds(new Set());
    setActiveFilters({});
    setPage(0);
  };

  const getActiveFilterLabels = (): Array<{
    type: string;
    label: string;
    filterKey: string;
  }> => {
    if (!filterData || Object.keys(activeFilters).length === 0) {
      return [];
    }

    const labels: Array<{ type: string; label: string; filterKey: string }> =
      [];

    if (activeFilters.commercialId) {
      const commercial = filterData.commercials.find(
        (c) => c.id.toString() === activeFilters.commercialId
      );
      if (commercial) {
        labels.push({
          type: 'Comercial',
          label: commercial.name,
          filterKey: 'commercialId',
        });
      } else {
        labels.push({
          type: 'Comercial',
          label: 'Desconocido',
          filterKey: 'commercialId',
        });
      }
    }

    if (activeFilters.managementId) {
      const idLookup =
        activeFilters.managementId === 'null'
          ? '-1'
          : activeFilters.managementId;
      const mgmt = filterData.managements?.find((m) => m.id === idLookup);
      if (mgmt) {
        labels.push({
          type: 'Gerencia',
          label: mgmt.name,
          filterKey: 'managementId',
        });
      } else {
        labels.push({
          type: 'Gerencia',
          label: 'Desconocido',
          filterKey: 'managementId',
        });
      }
    }

    if (activeFilters.producerId) {
      const producer = filterData.producers.find(
        (p) => p.id.toString() === activeFilters.producerId
      );
      if (producer) {
        labels.push({
          type: 'Usuario',
          label: producer.name,
          filterKey: 'producerId',
        });
      } else {
        labels.push({
          type: 'Usuario',
          label: 'Desconocido',
          filterKey: 'producerId',
        });
      }
    }

    if (activeFilters.referrerName) {
      const referrer = filterData.referrers.find(
        (r) => r.id.toString() === activeFilters.referrerName
      );
      if (referrer) {
        labels.push({
          type: 'Referido/Vendedor',
          label: referrer.name,
          filterKey: 'referrerName',
        });
      } else {
        labels.push({
          type: 'Referido/Vendedor',
          label: 'Desconocido',
          filterKey: 'referrerName',
        });
      }
    }

    if (activeFilters.pendingCommitteeRoleCode && committeeRoleFilterItems.length) {
      const role = committeeRoleFilterItems.find(
        (r) => r.roleCode === activeFilters.pendingCommitteeRoleCode
      );
      labels.push({
        type: 'Rol pendiente',
        label: role ? `${role.label} (${role.roleCode})` : activeFilters.pendingCommitteeRoleCode,
        filterKey: 'pendingCommitteeRoleCode',
      });
    }

    return labels;
  };

  const handleRemoveFilter = (filterKey: string) => {
    setSelectedIds(new Set());
    const newFilters = { ...activeFilters };
    delete newFilters[filterKey];
    setActiveFilters(newFilters);
    setPage(0);
  };

  return (
    <Card
      variant='neutral'
      modifier='inverted'
      spacing={'4'}
      height='-webkit-fill-available'
      width='85vw'
    >
      <Card display='flex' justifyContent='space-between'>
        <Card display='flex' alignItems='center'>
          <H withMargin={false} type='1' weight='bold' modifier='strong'>
            Hola, {name}
          </H>
        </Card>
      </Card>
      <Card display='flex' gap='16px' alignItems='center' margin={{ top: '4' }}>
        <Button variant='secondary-1' onClick={handleOpenModal}>
          <Card display='flex' alignItems='center' gap='8px'>
            <Icon
              name='other_filter'
              variant='secondary-1'
              width={20}
              height={20}
            />
            <P variant='secondary-1' withMargin={false}>
              Editar filtros
            </P>
          </Card>
        </Button>
        <Card width='400px' display='flex' gap='16px'>
          <Input
            type='text'
            size='md'
            icons={{ iconLeft: 'search_outline' }}
            placeholder='Buscar cliente'
            onChange={(e) => setSearchText(e.target.value)}
            onKeyup={() => handleSearch()}
          />
        </Card>
        {isBulkEnabled && (
          <Card display='flex' style={{ flex: 1, marginLeft: 'auto' }}>
            <CommitteeBulkActionsBar
              selectedCount={selectedIds.size}
              onApprove={handleClickApprove}
              onReject={handleClickReject}
              disabled={bulkPhase === 'processing'}
            />
          </Card>
        )}
      </Card>
      {Object.keys(activeFilters).length > 0 && (
        <div
          style={{
            display: 'flex',
            gap: '16px',
            alignItems: 'center',
            marginTop: '8px',
            flexWrap: 'wrap',
            backgroundColor: '#F5F7FA',
            padding: '16px 24px',
            borderRadius: '4px',
            border: '1px solid #E5E7EB',
          }}
        >
          {getActiveFilterLabels().map((filter, index) => (
            <div
              key={index}
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: '8px',
              }}
            >
              {filter.filterKey === 'status' ? (
                <>
                  <P
                    variant='neutral'
                    withMargin={false}
                    type='3'
                    weight='bold'
                  >
                    {filter.type}
                  </P>
                  <div
                    style={{
                      backgroundColor: '#A16108',
                      padding: '4px 12px',
                      borderRadius: '4px',
                      display: 'flex',
                      alignItems: 'center',
                      height: '24px',
                    }}
                  >
                    <P
                      variant='neutral'
                      modifier='inverted'
                      withMargin={false}
                      type='3'
                      weight='bold'
                      style={{
                        color: '#FFFFFF',
                        fontSize: '11px',
                        lineHeight: '16px',
                        letterSpacing: '0.3px',
                      }}
                    >
                      {filter.label.toUpperCase()}
                    </P>
                  </div>
                </>
              ) : (
                <>
                  <Icon
                    name='other_filter'
                    variant='secondary-1'
                    width={16}
                    height={16}
                  />
                  <P variant='neutral' withMargin={false} type='3'>
                    <span style={{ fontWeight: 'bold' }}>{filter.type}</span>{' '}
                    {filter.label}
                  </P>
                </>
              )}
            </div>
          ))}
          <div
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: '8px',
              cursor: 'pointer',
            }}
            onMouseEnter={() => setIsClearFiltersHovered(true)}
            onMouseLeave={() => setIsClearFiltersHovered(false)}
            onClick={handleClearFilters}
          >
            <Icon
              name='delete_outline'
              variant='neutral'
              width={20}
              height={20}
              style={{ color: activeFilters.status ? '#A16108' : '#585053' }}
            />
            <P
              variant='neutral'
              withMargin={false}
              type='3'
              style={{ color: activeFilters.status ? '#A16108' : '#585053' }}
            >
              Borrar filtros
            </P>
          </div>
        </div>
      )}
      {isSearching || isLoading ? (
        <Card margin={{ top: '4' }}>
          <TableSkeleton rows={10} columns={columns.length} />
        </Card>
      ) : applications.length > 0 ? (
        <Card margin={{ top: '4' }} justifyContent='center' alignItems='center'>
          <Table
            columns={columns}
            data={applications}
            tableData={tableData}
            handleChangePage={(e: any) => handleChangePage(e)}
            onRowClicked={(e: any) => handleRowClicked(e)}
          />
        </Card>
      ) : searchText !== '' ? (
        <Card
          display='grid'
          justifyContent='center'
          margin={{ top: '11', bottom: '11' }}
          width='53vw'
        >
          <Card display='flex' justifyContent='center' margin={{ bottom: '2' }}>
            <Image name='folder_empty' />
          </Card>
          <P variant='primary' align='center' withMargin={false} weight='bold'>
            No encontramos resultados.
          </P>
          <P variant='primary' align='center'>
            Podés hacer una nueva búsqueda.
          </P>
        </Card>
      ) : (
        <Card
          display='grid'
          justifyContent='center'
          margin={{ top: '11', bottom: '11' }}
          width='53vw'
        >
          <Card display='flex' justifyContent='center' margin={{ bottom: '2' }}>
            <Image name='folder_empty' />
          </Card>
          <P variant='primary' align='center' withMargin={false} weight='bold'>
            No hay carpetas en curso.
          </P>
          <P variant='primary' align='center'>
            Podés crear una nueva.
          </P>
          <Button variant='primary' onClick={() => navigate(routes.clients)}>
            Alta de nueva carpeta
          </Button>
        </Card>
      )}

      <FilterModal
        isOpen={isFilterModalOpen}
        onClose={handleCloseModal}
        onApplyFilters={handleApplyFilters}
        showCommercialFilter={!isManagementSelectionEnabled}
        showManagementFilter={isManagementSelectionEnabled}
        showProducerFilter={true}
        showReferrerFilter={true}
        showStatusFilter={false}
        showPendingCommitteeRoleFilter={isMultipleApprovalInCommitteeEnabled}
        pendingCommitteeRoleItems={committeeRoleFilterItems}
        initialFilters={activeFilters}
      />
      <CommitteeBulkConfirmModal
        isOpen={bulkPhase === 'confirmApprove' || bulkPhase === 'processing'}
        action='APPROVE'
        count={selectedIds.size}
        isProcessing={bulkPhase === 'processing'}
        onConfirm={handleConfirmApprove}
        onCancel={() => setBulkPhase('idle')}
      />
      <DeclineOrRequestChangesModal
        open={bulkPhase === 'commentReject'}
        onClose={() => {
          setBulkPhase('idle');
          setBulkRejectComment('');
        }}
        comment={bulkRejectComment}
        onCommentChange={setBulkRejectComment}
        selectionCount={selectedIds.size}
        maxLength={committeeCommentMaxLength}
        hideDeclineOption={shouldHideCommitteeDeclineOption(
          isCommitteeMemberQuorumEnabled,
          isAdmin,
          isRiskAnalyst,
        )}
        onDecline={handleBulkDecline}
        onRequestChanges={handleBulkRequestChanges}
      />
      {lastResult && (
        <CommitteeBulkResultModal
          isOpen={bulkPhase === 'result'}
          result={lastResult}
          action={lastBulkAction!}
          retryCount={retryCount}
          onClose={handleCloseResultModal}
          onRetryFailed={handleRetryFailed}
        />
      )}
    </Card>
  );
};

export default CommitteeDashboard;
